import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  SensorDescriptor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { ApptSegment, SegmentedAppointment } from '../segments';
import { TechLane, resolveTechColor } from '../useSchedulingData';
import { ScheduleException } from '../../../services/scheduleApi';
import ApptBlock from '../parts/ApptBlock';
import ExceptionBlock from '../parts/ExceptionBlock';
import { minutesOfDay, minutesToHeight, minutesToTop, pointToSlot, snapMinutesTo } from '../parts/laneMath';
import { bucketExceptionsByDate, PositionedException, slotStartLocal } from '../exceptionSegments';

export interface DropIntent {
  appointmentId: number;
  date: string;
  startMinutes: number;
  // Whether `date`/`startMinutes` actually differ from the appt's drag
  // origin (compared at the same 15-min snap resolution) — lets the drag
  // pipeline (useApptDrag) decide independently whether a reschedule write
  // is warranted, instead of always firing one on every drop.
  startChanged: boolean;
  technicianId?: number;
}

export interface WeekGridProps {
  weekStart: Dayjs;
  appts: SegmentedAppointment[];
  techs: TechLane[];
  exceptions: ScheduleException[];
  onOpen: (id: number) => void;
  onDrop?: (e: DropIntent) => void;
  sensors?: SensorDescriptor<any>[];
  /** Tap an existing availability block → edit it (gated by the caller). */
  onEditBlock?: (ex: ScheduleException) => void;
  /** Tap an empty slot → create a block prefilled with that day + 15-min start.
   *  Week columns have no tech lanes, so techId is always undefined here. */
  onCreateBlock?: (techId: number | undefined, startLocal: string) => void;
}

// Fallback shop window — per-tech schedule hours aren't wired into the data
// hook yet, so every column uses the same 8a-6p track until that lands.
const OPEN_MIN = 8 * 60;
const CLOSE_MIN = 18 * 60;
const TRACK_HEIGHT = 720;
const DAY_COUNT = 7;
const MIN_BLOCK_HEIGHT = 18;

function topFor(startMin: number): number {
  return minutesToTop(startMin, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT);
}

function heightFor(startMin: number, endMin: number): number {
  return minutesToHeight(startMin, endMin, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT, MIN_BLOCK_HEIGHT);
}

interface DaySegment {
  appt: SegmentedAppointment;
  segment: ApptSegment;
}

interface DragData {
  appointmentId: number;
  date: string;
  startMinutes: number;
  technicianId?: number;
}

interface DropData {
  date: string;
}

interface DraggableSegmentProps {
  appt: SegmentedAppointment;
  segment: ApptSegment;
  techColor?: string;
  techInitials?: string;
  onOpen: (id: number) => void;
}

function DraggableSegment({ appt, segment, techColor, techInitials, onOpen }: DraggableSegmentProps) {
  // Only the segment that begins the appointment can be dragged — dragging a
  // continuation slice (segIndex > 0) would only move part of a multi-day job.
  const canDrag = segment.segIndex === 0;
  const startMin = minutesOfDay(dayjs(segment.start));

  const dragData: DragData = {
    appointmentId: appt.id!,
    date: segment.date,
    startMinutes: startMin,
    technicianId: (appt as any).technician_id,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `appt-${appt.id}-${segment.segIndex}`,
    data: dragData,
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}
      style={{ opacity: isDragging ? 0.5 : 1, cursor: canDrag ? 'grab' : undefined, height: '100%' }}
    >
      <ApptBlock appt={appt} segment={segment} onOpen={onOpen} draggable={canDrag} techColor={techColor} techInitials={techInitials} />
    </div>
  );
}

interface DayColumnProps {
  dateStr: string;
  daySegments: DaySegment[];
  dayExceptions: PositionedException[];
  hourMarks: number[];
  onOpen: (id: number) => void;
  onEditBlock?: (ex: ScheduleException) => void;
  onCreateBlock?: (techId: number | undefined, startLocal: string) => void;
  label: string;
  techs: TechLane[];
}

function DayColumn({ dateStr, daySegments, dayExceptions, hourMarks, onOpen, onEditBlock, onCreateBlock, label, techs }: DayColumnProps) {
  const dropData: DropData = { date: dateStr };
  const { setNodeRef, isOver } = useDroppable({ id: `day-${dateStr}`, data: dropData });

  // Empty-slot tap → create a block at the tapped 15-min start. Only fires
  // when the click lands on the track itself (not on an appt/exception block,
  // whose own handlers stop here), and only when a create handler is wired.
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onCreateBlock) return;
    if (e.target !== e.currentTarget) return;
    const startMin = pointToSlot(e.nativeEvent.offsetY, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT);
    onCreateBlock(undefined, slotStartLocal(dateStr, startMin));
  };

  return (
    <div
      key={dateStr}
      data-testid={`day-${dateStr}`}
      style={{ flex: '1 0 140px', minWidth: 140, borderLeft: '1px solid #f0f0f0' }}
    >
      <div
        style={{
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 13,
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {label}
      </div>
      <div
        ref={setNodeRef}
        data-testid={`day-track-${dateStr}`}
        onClick={handleTrackClick}
        style={{
          position: 'relative',
          height: TRACK_HEIGHT,
          backgroundColor: isOver ? 'rgba(37,99,235,0.04)' : undefined,
          transition: 'background-color 0.1s',
          cursor: onCreateBlock ? 'copy' : undefined,
        }}
      >
        {hourMarks.map((h) => (
          <div
            key={h}
            style={{
              position: 'absolute',
              top: topFor(h * 60),
              left: 0,
              right: 0,
              borderTop: '1px solid #f5f5f5',
              pointerEvents: 'none',
            }}
          />
        ))}
        {/* Availability blocks render behind appointments (lower z-index). */}
        {dayExceptions.map((pos, i) => (
          <ExceptionBlock
            key={`ex-${pos.exception.id ?? 'x'}-${i}`}
            exception={pos.exception}
            top={pos.top}
            height={pos.height}
            onEdit={() => onEditBlock?.(pos.exception)}
          />
        ))}
        {daySegments.map(({ appt, segment }) => {
          const startMin = minutesOfDay(dayjs(segment.start));
          const rawEndMin = minutesOfDay(dayjs(segment.end));
          const endMin = rawEndMin > startMin ? rawEndMin : CLOSE_MIN;
          return (
            <div
              key={`${segment.appointmentId}-${segment.segIndex}`}
              style={{
                position: 'absolute',
                top: topFor(startMin),
                left: 2,
                right: 2,
                height: heightFor(startMin, endMin),
              }}
            >
              <DraggableSegment
                appt={appt}
                segment={segment}
                onOpen={onOpen}
                techColor={resolveTechColor(techs, appt.technician_id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeekGrid({ weekStart, appts, techs, exceptions, onOpen, onDrop, sensors, onEditBlock, onCreateBlock }: WeekGridProps) {
  const days = Array.from({ length: DAY_COUNT }, (_, i) => weekStart.add(i, 'day'));
  const rangeStart = days[0].format('YYYY-MM-DD');
  const rangeEnd = days[days.length - 1].format('YYYY-MM-DD');
  // Week columns merge all techs, so every tech's block for the day is shown.
  const exceptionsByDate = bucketExceptionsByDate(
    exceptions || [],
    rangeStart,
    rangeEnd,
    OPEN_MIN,
    CLOSE_MIN,
    TRACK_HEIGHT,
  );
  const hourMarks = Array.from(
    { length: (CLOSE_MIN - OPEN_MIN) / 60 + 1 },
    (_, i) => OPEN_MIN / 60 + i,
  );

  const fallbackSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );
  const activeSensors = sensors ?? fallbackSensors;

  const segmentsByDate = new Map<string, DaySegment[]>();
  for (const appt of appts) {
    for (const segment of appt.segments) {
      const list = segmentsByDate.get(segment.date) || [];
      list.push({ appt, segment });
      segmentsByDate.set(segment.date, list);
    }
  }

  // WeekGrid columns are per-day only (no tech lanes yet), so a drop never
  // implies a technician change — technicianId is intentionally omitted from
  // every DropIntent this grid produces.
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const dragData = active.data.current as DragData | undefined;
    const overData = over.data.current as DropData | undefined;
    if (!dragData || !overData) return;

    const activeRect = active.rect.current.translated;
    const overRect = (over as any).rect;
    let startMinutes = dragData.startMinutes;
    if (activeRect && overRect && typeof overRect.top === 'number') {
      const offsetY = activeRect.top - overRect.top;
      startMinutes = pointToSlot(offsetY, OPEN_MIN, CLOSE_MIN, TRACK_HEIGHT);
    }

    const startChanged = overData.date !== dragData.date || startMinutes !== snapMinutesTo(dragData.startMinutes);
    if (!startChanged) return;

    onDrop?.({
      appointmentId: dragData.appointmentId,
      date: overData.date,
      startMinutes,
      startChanged,
    });
  }

  return (
    <DndContext sensors={activeSensors} onDragEnd={handleDragEnd} autoScroll>
      <div style={{ overflowX: 'auto', width: '100%' }} data-testid="week-grid">
        <div style={{ display: 'flex', minWidth: 7 * 140 + 56 }}>
          <div style={{ width: 56, flexShrink: 0 }}>
            <div style={{ height: 40 }} />
            <div style={{ position: 'relative', height: TRACK_HEIGHT }}>
              {hourMarks.map((h) => (
                <div
                  key={h}
                  style={{
                    position: 'absolute',
                    top: topFor(h * 60) - 6,
                    right: 8,
                    fontSize: 11,
                    color: 'rgba(0,0,0,0.45)',
                  }}
                >
                  {dayjs().hour(h).minute(0).format('h A')}
                </div>
              ))}
            </div>
          </div>

          {days.map((day) => {
            const dateStr = day.format('YYYY-MM-DD');
            return (
              <DayColumn
                key={dateStr}
                dateStr={dateStr}
                daySegments={segmentsByDate.get(dateStr) || []}
                dayExceptions={exceptionsByDate.get(dateStr) || []}
                hourMarks={hourMarks}
                onOpen={onOpen}
                onEditBlock={onEditBlock}
                onCreateBlock={onCreateBlock}
                label={day.format('ddd D')}
                techs={techs}
              />
            );
          })}
        </div>
      </div>
    </DndContext>
  );
}

export default WeekGrid;
